import { useEffect, useState } from "react";
import { TableData, TableDataField, TableHeader } from "../../../../components/Table/DataTable";
import PlotlyGraphs from "../../../../components/Data/PlotlyGraphs";
import { calculateConformalMode, getConformalValue, isConformalMethodLabel } from "../../../../utils/formattingFns";
import { Skeleton } from "@mui/material";


export default function FrequencyVsAlphaIndividual({
    data, 
    threshold,
    headers,
}: {
    data: TableData[],
    threshold: number,
    headers: TableHeader[],
}) {

    const [ plotData, setPlotData ] = useState<{ data: Plotly.Data[], method: string }[]>([]);

    const formatConfData = (records: TableData[], threshold: number) => {
        if (records.length > 0) {
            let methodNames: string[] = [];
            records[0].fields.forEach(field => {
                const matchingHeader = headers.find(header => header.value === field.associatedColumn);
                if (matchingHeader && isConformalMethodLabel(matchingHeader.label)) {
                    methodNames.push(matchingHeader.label)
                }
            });

            const sortedMethods = methodNames.sort((a,b) => {
                if (a.toLocaleLowerCase() < b.toLocaleLowerCase()) {
                    return -1;
                } else if (a.toLocaleLowerCase() > b.toLocaleLowerCase()) {
                    return 1;
                } else {
                    return 0;
                }
            });

            const methods = ['mode'].concat(sortedMethods).flat();

            if (methods.length > 0) {
                const alphaScores = [0.05, 0.1, 0.15, 0.2, 0.25, 0.3,0.35, 0.4, 0.45, 0.5, 0.55,0.6,0.65,0.7, 0.75, 0.8, 0.85, 0.9,  0.95];
                let plots: {data: Plotly.Data[], method: string}[] = [];
                methods.forEach(method => {
                    let traces: Plotly.Data[] = [];
                    let fields: TableDataField[] = [];
                    let frequencies: {
                        OOD: number,
                        INC: number,
                        INA: number,
                        ACT: number,
                    }[] = [];
    
                    if (method !== 'mode') {
                        records.forEach(record => {
                            const matchingHeader = headers.find(header => header.label === method);
                            if (matchingHeader) {
                                const matchingMethod = record.fields.find(field => field.associatedColumn === matchingHeader.value);
                                if (matchingMethod) {
                                    fields.push(matchingMethod);
                                }
                            }
                        });
    
                        alphaScores.forEach(score => {
                            let OOD = 0;
                            let INC = 0;
                            let INA = 0;
                            let ACT = 0;
        
                            fields.forEach(field => {
                                const value = field.value as number[];
                                const { label } = getConformalValue(value, score);
                                if (label === 'Out of Domain') {
                                    OOD += 1;
                                } else if (label === 'Inconclusive') {
                                    INC += 1;
                                } else if (label === 'Inactive') {
                                    INA += 1;
                                } else if (label === 'Active') {
                                    ACT += 1;
                                }
                            });
                            
        
                            frequencies.push({OOD, INC, INA, ACT});
        
                        });
    
                    } else {
    
                        alphaScores.forEach(score => {
                            let OOD = 0;
                            let INC = 0;
                            let INA = 0;
                            let ACT = 0;
                            records.forEach(record => {
                                const { label } = calculateConformalMode(record.fields, headers, score);
                                if (label === 'Out of Domain') {
                                    OOD += 1;
                                } else if (label === 'Inconclusive') {
                                    INC += 1;
                                } else if (label === 'Inactive') {
                                    INA += 1;
                                } else if (label === 'Active') {
                                    ACT += 1;
                                }
                            });
    
                            frequencies.push({OOD, INC, INA, ACT});
                        })
                
                    };

                    const closestIndex = alphaScores.reduce((prev, curr, index) =>
                        Math.abs(curr - threshold) < Math.abs(alphaScores[prev] - threshold) ? index : prev,
                        0);
                        const highlightX = alphaScores[closestIndex];
    
    
                    traces = [
                        {
                            x: alphaScores,
                            y: frequencies.map(f => f.OOD),
                            name: 'Out of Domain',
                            type: 'bar',
                            legendgroup: 'group1',
                            marker: {
                                color: 'rgba(190, 190, 190, 0.6)',
                                line: {
                                    color: 'rgba(190, 190, 190, 1)',
                                    width: 2,
                                }
                            },
                            
                            
                            
                        },
                        {
                            x: alphaScores,
                            y: frequencies.map(f => f.OOD),
                            name: 'Out of Domain',
                            type: 'scatter',
                            mode: 'lines',
                            legendgroup: 'group1',
                            line: {
                                color: 'rgba(190, 190, 190, 1)',
                                smoothing: 0.7,
                                shape: 'spline',
                                width: 3,
                            },
                        },
                        {
                            x: alphaScores,
                            y: frequencies.map(f => f.INC),
                            name: 'Inconclusive',
                            type: 'bar',
                            legendgroup: 'group2',
                            marker: {
                                color: 'rgba(255 192 97, 0.6)',
                                line: {
                                    color: 'rgba(255 192 97, 1)',
                                    width: 2,
                                }
                                
                            }
                        },
                        {
                            x: alphaScores,
                            y: frequencies.map(f => f.INC),
                            name: 'Inconclusive',
                            type: 'scatter',
                            mode: 'lines',
                            legendgroup: 'group2',
                            line: {
                                color: 'rgba(255 192 97, 1)',
                                smoothing: 0.7,
                                shape: 'spline',
                                width: 3,
                            }
                        },
                        {
                            x: alphaScores,
                            y: frequencies.map(f => f.INA),
                            name: 'Inactive',
                            type: 'bar',
                            legendgroup: 'group3',
                            marker: {
                                color: 'rgba(255 132 132, 0.6)',
                                line: {
                                    color: 'rgba(255 132 132, 1)',
                                    width: 2,
                                }
                                
                            }
                        },
                        {
                            x: alphaScores,
                            y: frequencies.map(f => f.INA),
                            name: 'Inactive',
                            type: 'scatter',
                            mode: 'lines',
                            legendgroup: 'group3',
                            line: {
                                color: 'rgba(255 132 132, 1)',
                                smoothing: 0.7,
                                shape: 'spline',
                                width: 3,
                            }
                        },
                        {
                            x: alphaScores,
                            y: frequencies.map(f => f.ACT),
                            name: 'Active',
                            type: 'bar',
                            legendgroup: 'group4',
                            marker: {
                                color: 'rgba(123 201 96, 0.6)',
                                line: {
                                    color: 'rgba(123 201 96, 1)',
                                    width: 2,
                                }
                                
                            }
                        },
                        {
                            x: alphaScores,
                            y: frequencies.map(f => f.ACT),
                            name: 'Active',
                            type: 'scatter',
                            mode: 'lines',
                            legendgroup: 'group4',
                            line: {
                                color: 'rgba(123 201 96, 1)',
                                smoothing: 0.7,
                                shape: 'spline',
                                width: 3,
                            }
                        },
                        {
                            x: alphaScores,
                            y: alphaScores.map(score => (score === highlightX ? records.length : 0)),
                            name: 'Current Threshold',
                            type: 'bar',
                            legendgroup: 'group5',
                            marker: {
                                color: 'rgba(31, 119, 180, 0.2)',
                            }
                        },
                    ];
    
                    plots.push({data: traces, method});
                });
    
                setPlotData(plots);
            }
        }
    };

    useEffect(() => {
        if (threshold !== null && threshold !== undefined && data.length > 0) {
            formatConfData(data, threshold);
        }

        //eslint-disable-next-line
    }, [threshold, data, headers]);


    return (
        <div className="w-full p-6 flex justify-between items-start gap-6">
            <div className='grow conf-pred-plot-grid'>
                {plotData.length > 0 ? (
                    <>
                    {plotData.map(plot => (
                        <div className="w-full h-full rounded-lg border border-outline overflow-hidden p-4">
                            <PlotlyGraphs data={plot.data} type='conf' isSquare={true} methodName={plot.method} total={data.length}/>
                        </div>
                    ))}
                    </>
                ) : (
                    <>
                    {Array.from(Array(8).keys()).map(key => (
                        <div className="w-full aspect-square rounded-lg overflow-hidden"><Skeleton variant='rounded' width='100%' height={400}/></div>
                    ))}
                    </>
                )}
                
            </div>
        </div>
    )
}